/**
 * Lightweight sanitizer for inline ```html ... ``` blocks.
 *
 * Goal: stop the model from accidentally breaking the chat or
 * exfiltrating data. NOT a full XSS shield — we still trust the model
 * within reason. Anything that affects the global page or loads
 * external resources gets stripped; anything self-contained passes
 * through.
 *
 * What gets stripped:
 *  - <iframe>, <object>, <embed> — nested document loaders
 *  - <meta>, <link>, <base> — global page metadata + external resources
 *  - javascript: URLs in href / src / formaction → blocked:
 *  - Global element selectors (html, body, *, :root, head) inside
 *    <style> blocks — these would bleed into the whole app
 *
 * What stays:
 *  - Inline <style> with class-scoped rules
 *  - Inline <script> (the renderer re-creates these to execute them)
 *  - Tailwind class attributes
 *  - <img src="https://..."> (allowed — display-only)
 *  - data: URLs in src (fine — self-contained)
 */
export function sanitizeInlineHtml(input: string): string {
  let html = input;

  // Strip dangerous nested-document / resource-loading tags.
  html = html.replace(/<iframe\b[^>]*>[\s\S]*?<\/iframe\s*>/gi, '<!-- iframe stripped -->');
  html = html.replace(/<object\b[^>]*>[\s\S]*?<\/object\s*>/gi, '<!-- object stripped -->');
  html = html.replace(/<embed\b[^>]*\/?>/gi, '<!-- embed stripped -->');
  html = html.replace(/<meta\b[^>]*\/?>/gi, '<!-- meta stripped -->');
  html = html.replace(/<link\b[^>]*\/?>/gi, '<!-- link stripped -->');
  html = html.replace(/<base\b[^>]*\/?>/gi, '<!-- base stripped -->');

  // Neutralize javascript: URLs everywhere they might appear.
  html = html.replace(/(\s(?:href|src|formaction|action|xlink:href)\s*=\s*)(["'])\s*javascript:/gi, '$1$2blocked:');

  // Strip CSS rules that target global element selectors inside <style>
  // blocks. Anything scoped to a class survives. The match is conservative
  // — we only strip rules whose selector list contains a bare global
  // element name; mixed selectors like "h1.my-card" pass through.
  html = html.replace(/<style\b([^>]*)>([\s\S]*?)<\/style\s*>/gi, (_, attrs, body) => {
    const cleaned = stripGlobalCssRules(body);
    return `<style${attrs}>${cleaned}</style>`;
  });

  return html;
}

/**
 * Walk top-level CSS rules and drop any whose selector list is JUST a
 * bare global element selector (html, body, *, :root, head). Compound
 * selectors that include a class (e.g. `.foo h1`, `h1.title`) pass.
 */
function stripGlobalCssRules(css: string): string {
  const BARE_GLOBAL = /^(?:html|body|\*|:root|head)$/i;
  let out = '';
  let depth = 0;
  let buffer = '';

  for (let i = 0; i < css.length; i += 1) {
    const ch = css[i];
    buffer += ch;
    if (ch === '{') {
      depth += 1;
    } else if (ch === '}') {
      depth -= 1;
      if (depth === 0) {
        // Buffer holds one complete rule (or @-block).
        const flushed = filterRule(buffer, BARE_GLOBAL);
        out += flushed;
        buffer = '';
      }
    }
  }
  // Trailing whitespace / leftover (no closing brace seen) — pass through.
  out += buffer;
  return out;
}

function filterRule(rule: string, banned: RegExp): string {
  const braceIdx = rule.indexOf('{');
  if (braceIdx === -1) return rule;
  const selectorList = rule.slice(0, braceIdx).trim();
  // @media / @supports / @keyframes etc. — keep as-is.
  if (selectorList.startsWith('@')) return rule;

  const selectors = selectorList.split(',').map((s) => s.trim()).filter(Boolean);
  if (selectors.length === 0) return rule;

  const allBareGlobal = selectors.every((s) => banned.test(s));
  if (allBareGlobal) {
    return `/* global selector stripped: ${selectorList.slice(0, 60)} */`;
  }
  return rule;
}
